import "./ImportSectionModal.scss";

import clsx from "clsx";
import { FC, useState } from "react";

import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { ImportSectionRequestType } from "components/server-types";
import { t } from "i18n/i18n";
import { CancelSaveWidget } from "mds/components/CancelSaveWidget";
import { Checkbox } from "mds/components/Checkbox";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import { ListItem } from "mds/components/ListItem";
import { BookSectionIcon, ChevronDownIcon, ChevronUpIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectSectionsByTermAndCourse } from "store/selectors";
import { useListSelector } from "store/store-hooks";

export interface ImportSectionModalProps {
  onCancel: () => void | undefined;
  onConfirm: (data: ImportSectionRequestType) => void | undefined;
}

export const ImportSectionModal: FC<ImportSectionModalProps> = ({ onCancel, onConfirm }) => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const showSections = selectedTermId !== "" && selectedCourseId !== "";

  const sections = useListSelector((s) =>
    selectSectionsByTermAndCourse(s, selectedTermId, selectedCourseId),
  );

  interface ContentOptionProps {
    label: "topics" | "assignments" | "course_resources" | "instructor_workspace";
    selected: boolean;
  }

  enum ContentOption {
    TOPICS,
    ASSIGNMENTS,
    SECTION_RESOURCES,
    INSTRUCTOR_WORKSPACE,
  }

  const [contentOptions, setContentOptions] = useState<ContentOptionProps[]>([
    { label: "topics", selected: true },
    { label: "assignments", selected: true },
    { label: "course_resources", selected: true },
    { label: "instructor_workspace", selected: true },
  ]);
  const allOptionsSelected = contentOptions.every((option) => option.selected);
  const noOptionsSelected = contentOptions.every((option) => !option.selected);

  const handleCourseChange = (newCourseId: string) => {
    setSelectedCourseId(newCourseId);
  };

  const handleTermChange = (newTermId: string) => {
    setSelectedTermId(newTermId);
  };

  const handleSectionChange = (newSectionId: string) => {
    setSelectedSectionId(newSectionId);
  };

  const handleContentOptionsChange = (index) => {
    //   set the selected option to the opposite of what it currently is
    const newOptions = contentOptions.map((option, i) => {
      if (i === index) {
        return { ...option, selected: !option.selected };
      }

      return option;
    });

    // Update the state with the new options array
    setContentOptions(newOptions);
  };

  const handleAllContentOptionsChange = () => {
    setContentOptions(
      contentOptions.map((option) => ({ ...option, selected: !allOptionsSelected })),
    );
  };

  const toggleShowOptions = () => {
    setShowOptions(!showOptions);
  };

  // TODO: when the template nav is clicked update the list of sections and set the tab to active
  return (
    <div className="import-modal w-full">
      <div className="section-options mt-4 flex items-center gap-2 py-4">
        <TermMenu menuType="menu" selectedTermId={selectedTermId} onTermChange={handleTermChange} />

        <CourseMenu
          menuType="menu"
          selectedCourseId={selectedCourseId}
          onCourseChange={handleCourseChange}
        />
      </div>

      <div className={clsx("section-list mb-2", { hidden: !showSections })}>
        {/* TODO: add empty state if the selected and term and course code has no sections */}
        {sections
          .filter((section) => section.id !== currentSectionId)
          .map((section) => (
            <div key={section.id}>
              <ListItem
                className="min-w-0"
                isSelected={selectedSectionId === section.id}
                onClick={() => handleSectionChange(section.id)}
              >
                <IconText
                  className="w-full"
                  iconStart={<BookSectionIcon />}
                  text={<div className="min-w-0 truncate">{section.title}</div>}
                />
              </ListItem>
            </div>
          ))}
      </div>

      <div className={clsx("p-2", { hidden: !showSections })}>
        <div className="flex items-center gap-2 pb-1">
          <Checkbox checked={allOptionsSelected} onChange={handleAllContentOptionsChange}>
            <span>{t("import_modal.include")}</span>
          </Checkbox>

          <LegacyButton
            kind="tertiary"
            size="xs"
            title={showOptions ? t("import_modal.hide_options") : t("import_modal.show_options")}
            iconOnly
            onClick={toggleShowOptions}
          >
            {showOptions ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </LegacyButton>
        </div>

        {showOptions &&
          contentOptions.map((option: ContentOptionProps, index) => (
            <div className="ml-4 flex gap-2 pb-1" key={option.label}>
              <Checkbox
                checked={option.selected}
                onChange={() => {
                  handleContentOptionsChange(index);
                }}
              >
                <span>{t(`glossary.${option.label}`)}</span>
              </Checkbox>
            </div>
          ))}
      </div>

      <CancelSaveWidget
        className="right mt-4"
        isSaveDisabled={selectedSectionId === null || noOptionsSelected}
        saveTextKey="import_section"
        onClickCancel={onCancel}
        onClickSave={() => {
          if (selectedSectionId != null && currentSectionId != null) {
            onConfirm?.({
              source_section_id: selectedSectionId,
              target_section_id: currentSectionId,
              include_topics: contentOptions[ContentOption.TOPICS].selected,
              include_assignments: contentOptions[ContentOption.ASSIGNMENTS].selected,
              include_course_resources: contentOptions[ContentOption.SECTION_RESOURCES].selected,
              include_workspace: contentOptions[ContentOption.INSTRUCTOR_WORKSPACE].selected,
            });
          }
        }}
      />
    </div>
  );
};
